import { openDB, deleteDB } from 'idb';
import { writeConsent } from '@referee/analytics';
import type { ITestData, RefereeDB } from '../model/types';
import type { IXpProfile, XpDB } from '../model/xp-types';
import { LEVELS } from '../model/xp-constants';
import { loadQuestions } from '../utils/loadTestData';
import i18n from '../i18n';
import type { ScreenshotScenario } from './scenario';

/**
 * D-14 deterministic demo-progress seed for store screenshots. Pure and
 * index-derived — no non-deterministic randomness — so the same (ids, now)
 * always produces byte-identical output, whatever the locale.
 */

const SEED_COUNT = 150;
const MS_PER_DAY = 86_400_000;

// Gives a believable box distribution: mostly boxes 1-3, some 4, a few
// fully-mastered box 5 — never all-empty, never all-mastered.
const BOX_PATTERN = [1, 2, 2, 3, 3, 3, 4, 4, 5, 1];

export function buildSeedRecords(ids: string[], now: number): Record<string, ITestData> {
  const sorted = [...ids].sort((a, b) => a.localeCompare(b, 'en'));
  const selected = sorted.slice(0, SEED_COUNT);

  const records: Record<string, ITestData> = {};

  selected.forEach((id, i) => {
    const box = BOX_PATTERN[i % BOX_PATTERN.length];
    const asked = 1 + (i % 4) + (box - 1);
    const correct = Math.max(0, asked - (i % 3 === 0 ? 1 : 0) - (box === 1 ? 1 : 0));
    const wrong = asked - correct;
    const resultsLength = Math.min(asked, 5);
    const previousResults = Array.from(
      { length: resultsLength },
      (_, k) => (k < resultsLength - 1 ? (i + k) % 3 !== 0 : box > 1),
    );
    const lastAsked = new Date(now - ((i % 20) + 1) * MS_PER_DAY);

    records[id] = { box, lastAsked, previousResults, asked, correct, wrong };
  });

  return records;
}

function toIsoDate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

// Midpoint of level 4's XP range: comfortably inside the required 3-5
// window, with far more than 50 XP of headroom before level 5 — a single
// seeded correct-answer capture can never trigger a level-up.
function pickSeedTotalXp(): number {
  const level4 = LEVELS.find((l) => l.level === 4);
  const level5 = LEVELS.find((l) => l.level === 5);
  if (!level4 || !level5) {
    return 0;
  }
  return level4.xpRequired + Math.floor((level5.xpRequired - level4.xpRequired) / 2);
}

export function buildSeedXpProfile(now: number): IXpProfile {
  const todayIso = toIsoDate(now);
  const practiceDates = Array.from(
    { length: 6 },
    (_, k) => toIsoDate(now - (5 - k) * MS_PER_DAY),
  );

  return {
    totalXp: pickSeedTotalXp(),
    practiceDates,
    currentDailyStreak: 6,
    bestDailyStreak: 6,
    lastPracticeDate: todayIso,
    masteredCategories: [],
  };
}

export async function applyScreenshotSeed(scenario: ScreenshotScenario): Promise<void> {
  await deleteDB(scenario.catalog);
  await deleteDB('referee_xp');

  const { answers } = await loadQuestions('en', scenario.catalog);
  const ids = answers.map((a) => a.id);

  const now = Date.now();
  const records = buildSeedRecords(ids, now);

  const questionsDb = await openDB<RefereeDB>(scenario.catalog, 1, {
    upgrade(db) {
      db.createObjectStore('questions');
    },
  });

  const questionsTx = questionsDb.transaction('questions', 'readwrite');
  for (const [id, record] of Object.entries(records)) {
    await questionsTx.store.put(record, id);
  }

  const scenarioQuestionRecord: ITestData = {
    box: 2,
    lastAsked: new Date(now - MS_PER_DAY),
    previousResults: [true, false, true],
    asked: 3,
    correct: 2,
    wrong: 1,
  };
  await questionsTx.store.put(scenarioQuestionRecord, scenario.questionId);
  await questionsTx.done;
  questionsDb.close();

  const xpDb = await openDB<XpDB>('referee_xp', 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('xpProfile')) {
        db.createObjectStore('xpProfile');
      }
      if (!db.objectStoreNames.contains('xpEvents')) {
        db.createObjectStore('xpEvents', { autoIncrement: true });
      }
    },
  });
  await xpDb.put('xpProfile', buildSeedXpProfile(now), 'profile');
  xpDb.close();

  writeConsent({ analytics: false, sessionReplay: false });

  await i18n.changeLanguage(scenario.lang);
}
